import { Navigate, useNavigate } from "@tanstack/react-router";
import { useClerk, useUser } from "@clerk/react";
import { INDUSTRY_CODES, type IndustryType } from "@amsaas/shared/constants";
import { isClientType } from "@amsaas/shared/types";
import { useActiveUser } from "../hooks/useActiveUser";
import { usePlatformAuth } from "../hooks/usePlatformAuth";
import { platformTrpc } from "../lib/platform-trpc";
import {
  getAppHost,
  audienceAllowedOnHost,
  otherAppUrl,
  type AppHost,
} from "../lib/appHost";
import { Button } from "@/components/ui/button";
import { isAmcaasHost, AppHostGate } from "./auth/dispatch";
import { AmsaasLandingPage } from "./landing/amsaasLanding";
import { AmcaasLandingPage } from "./landing/amcaasLanding";

/**
 * Root redirect by identity after auth resolves (05-frontend-design.md §4).
 *
 * There are TWO authenticated identities:
 *   - a TENANT user (provider/client) — has an org in the Clerk token; resolved
 *     via auth.me (ENH-004: the ACTIVE tenant's role).
 *   - a self-service PLATFORM user (ENH-007) — no org, so auth.me cannot resolve
 *     a tenant; resolved via the platform getProfile.
 *
 * Tenant identity takes precedence. CRITICAL: a signed-in user is NEVER sent to
 * /login — Clerk's <SignIn> bounces an active session straight back to "/", so
 * redirecting a signed-in user there is an infinite refresh loop. A signed-in
 * user with no resolvable identity (e.g. a Clerk account whose platform_users
 * row was never created because the user.created webhook isn't wired) gets an
 * explicit "account not set up" screen instead.
 *
 * - not signed in              → /login
 * - provider_admin / _user     → /provider/dashboard
 * - client_admin / _user / personal_user → /portal/dashboard
 * - platform user, pending     → /platform/verify-email
 * - platform user, verified/migrated → /marketplace
 * - signed in, no identity     → "account not set up" screen (no redirect)
 */
export function RootRedirect(): JSX.Element {
  const tenant = useActiveUser();
  const platform = usePlatformAuth();

  // eslint-disable-next-line no-console -- temporary auth debug
  console.log("[auth-debug] RootRedirect", {
    tenantSignedIn: tenant.isSignedIn,
    tenantLoading: tenant.isLoading,
    tenantUserType: tenant.userType,
    tenantError: tenant.isError,
    platformLoading: platform.isLoading,
    platformStatus: platform.status,
  });

  // Signed-out visitor at "/" → the app's marketing landing (host-aware), NOT a
  // redirect to /login. The landing's "doors" link to the neutral login routes.
  if (!tenant.isSignedIn && !tenant.isLoading) {
    return (
      <AppHostGate>
        <HostLanding />
      </AppHostGate>
    );
  }
  if (tenant.isLoading || platform.isLoading) {
    return <div className="p-10 text-slate-500">Loading…</div>;
  }

  const host = getAppHost();

  // 1) Active tenant identity wins (the token carries an org).
  if (tenant.userType) {
    // Host-as-trust-boundary (AUTH-06/08, GAP-025): reject an identity with no
    // valid record for THIS app before routing it into a shell.
    if (
      tenant.appAudience &&
      !audienceAllowedOnHost(tenant.appAudience, host)
    ) {
      return <WrongApp host={host} />;
    }
    return isClientType(tenant.userType) ? (
      <Navigate to="/portal/dashboard" />
    ) : (
      <Navigate to="/provider/dashboard" />
    );
  }

  // 2) Otherwise, a self-service platform identity (no org / no tenant user) —
  //    B2C only, so reject on the B2B host.
  if (platform.status === "pending_verification") {
    return host === "b2b" ? (
      <WrongApp host={host} />
    ) : (
      <Navigate to="/platform/verify-email" />
    );
  }
  if (platform.status === "verified" || platform.status === "migrated") {
    return host === "b2b" ? (
      <WrongApp host={host} />
    ) : (
      <Navigate to="/platform/dashboard" />
    );
  }

  // 3) Signed in but neither a tenant user nor a platform account. Do NOT
  //    redirect to /login (would loop) — show an actionable terminal screen.
  return <AccountNotSetUp />;
}

/** The app's marketing landing, picked by host (B2C → AMCaaS, else AMSaaS). */
function HostLanding(): JSX.Element {
  return isAmcaasHost() ? <AmcaasLandingPage /> : <AmsaasLandingPage />;
}

/**
 * Host-as-trust-boundary rejection (AUTH-06/08, GAP-025): the signed-in identity
 * has no valid record for THIS app. Point them at the correct app; never bounce
 * to /login (would loop an active Clerk session).
 */
function WrongApp({ host }: { host: AppHost }): JSX.Element {
  const { signOut } = useClerk();
  const url = otherAppUrl("/");
  const thisApp = host === "b2b" ? "the business app" : "the marketplace app";
  const otherApp = host === "b2b" ? "the marketplace app" : "the business app";

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <img
        src="/Logo.svg"
        alt="AMSaaS"
        className="w-40 max-w-full object-contain"
      />
      <h1 className="text-lg font-semibold">
        This account isn’t available on {thisApp}
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Your account belongs on {otherApp}.{" "}
        {url
          ? "Continue there to sign in."
          : "Open the correct app to sign in."}
      </p>
      <div className="flex gap-2">
        {url && (
          <Button asChild>
            <a href={url}>Go to {otherApp}</a>
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => void signOut({ redirectUrl: "/login" })}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}

function AccountNotSetUp(): JSX.Element {
  const { signOut } = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();

  const complete = platformTrpc.completeRegistration.useMutation({
    onSuccess: () => navigate({ to: "/platform/dashboard" }),
  });

  const finishSetup = (): void => {
    const meta = (user?.unsafeMetadata ?? {}) as {
      companyName?: unknown;
      industryTypes?: unknown;
    };
    const known = INDUSTRY_CODES as readonly string[];
    const industryTypes = Array.isArray(meta.industryTypes)
      ? meta.industryTypes.filter(
          (x): x is IndustryType => typeof x === "string" && known.includes(x),
        )
      : [];
    complete.mutate({
      companyName:
        typeof meta.companyName === "string" ? meta.companyName : undefined,
      industryTypes,
    });
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <img
        src="/Logo.svg"
        alt="AMSaaS"
        className="w-40 max-w-full object-contain"
      />
      <h1 className="text-lg font-semibold">Your account isn’t set up yet</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        You’re signed in, but this account isn’t linked to a provider or a
        platform profile. Finish setting up your platform account, or sign out
        and try a different login.
      </p>
      {complete.error && (
        <p className="max-w-md text-sm text-destructive">
          {complete.error.message}
        </p>
      )}
      <div className="flex gap-2">
        <Button disabled={complete.isPending} onClick={finishSetup}>
          {complete.isPending ? "Setting up…" : "Finish setting up my account"}
        </Button>
        <Button
          variant="outline"
          onClick={() => void signOut({ redirectUrl: "/login" })}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
